<template>
  <div class="row mw-node-card-main">
    <div class="diagnostic-progress-bar">
      <DiagnosticProgressBar v-if="isDiag && !showSearch" />
    </div>
    <!-- SEARCH OUTPUT DISPLAY -->
    <div class="col-12 navigation-main-card" v-if="showSearch">
      <div v-if="displaySearchResults">
        <div
          class="row mw-node-card-main-item py-2 m-2"
          :class="{ hover: hovered === index }"
          v-for="(item, index) in searchResults"
          :key="item.id"
          @mouseenter="onHover(index)"
          @mouseleave="hovered = -1"
          @click.prevent="clickItem(item, index, $event)"
        >
          <div class="col-12 d-flex align-items-start">
            <div class="icon-wrapper">
              <font-awesome-icon
                v-if="item.__typename === 'Case'"
                class="large-icon grey"
                :icon="['fad', 'folder']"
              />
              <font-awesome-icon
                v-else-if="item.type === 'Step'"
                class="large-icon blue fa-rotate-90"
                :icon="['fas', 'level-up']"
              />
              <font-awesome-icon
                v-else-if="item.type === 'keyStep'"
                class="large-icon blue"
                :icon="['fad', 'key']"
              />

              <img v-else :src="getIcon(item.type)" alt class="large-icon" />
              <el-tooltip
                v-if="item.userBoost === '90'"
                placement="top"
                effect="light"
                :content="$t(`${getBoostedLevel(item.userBoost).tooltip}`)"
                ><div
                  class="user-boosted-result"
                  :style="getBoostedLevel(item.userBoost).customStyle"
                >
                  <font-awesome-icon
                    class="user-boosted-result-icon"
                    :icon="['fas', 'rocket']"
                  /></div
              ></el-tooltip>
            </div>

            <div class="label-row flex-column">
              <div
                class="d-flex align-items-center justify-content-between gap-4"
              >
                <el-tooltip
                  :disabled="isTooltipDisabled(item.id)"
                  :content="item.label"
                  placement="top"
                  effect="light"
                >
                  <h3 class="mb-0 text-overflow" :ref="item.id">
                    {{ item.label }}
                  </h3></el-tooltip
                >
                <div class="flex align-items-center">
                  <div
                    v-if="displaySummary(item) && hovered === index"
                    :class="[
                      'summary-button',
                      { opened: openedSummaries.includes(item.id) },
                    ]"
                    @click.stop="toggleSummary(item.id)"
                  >
                    <font-awesome-icon
                      :icon="['fal', 'file-alt']"
                      :class="['summary-icon']"
                    />
                    {{ $t('knowledge.search-admin.summary') }}
                  </div>
                  <ExplainPromptPopover
                    :explain="item.explain"
                    class="explain-prompt"
                  />
                </div>
              </div>
              <div
                :class="[
                  'search-summary',
                  {
                    hovered: hovered === index,
                    opened: openedSummaries.includes(item.id),
                  },
                ]"
                v-if="displaySummary(item)"
              >
                <div>{{ getSummary(item) }}</div>
              </div>
              <breadcrumb :items="item.breadcrumb" class="mt-0">
                <ProductParameters
                  v-if="item.denormalizedParameters"
                  :parameters="item.denormalizedParameters"
                />
              </breadcrumb>
              <div
                class="d-flex align-items-center full-labels mr-1"
                v-if="item.labels && item.labels.length"
              >
                <ContentListItemFullLabelWrapper
                  v-if="item.labels && item.labels.length"
                  :labelSearchedIds="labelSearchedIds"
                  :labels="item.labels"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="hasMore || loading"
          class="d-flex justify-content-center mb-3"
        >
          <el-button
            @click="searchDisplayMore"
            :loading="loading"
            size="small"
            plain
            type="primary"
            >{{ $t('knowledge.search-admin.show-more') }}</el-button
          >
        </div>
      </div>
      <div v-else>
        <div
          class="align-self-center d-flex flex-column align-items-center py-8"
        >
          <div class="search-icon">
            <font-awesome-icon
              v-if="loading"
              :icon="['fal', 'spinner-third']"
              spin
            />

            <font-awesome-icon v-else :icon="['fas', 'search']" />
          </div>
          <span v-if="!loading" class="d-flex col-6 justify-content-center">
            <h2 class="text-center search-empty-state">
              {{ $t('mayday-web.search-empty') }}

              <strong style="color: black; font-weight: bold">
                {{ searchQuery }}
              </strong>
              <strong
                v-if="searchFilters.length"
                style="color: black; font-weight: bold"
              >
                <span v-if="searchQuery">
                  {{ ' ' }}{{ $t('mayday-web.search-empty-and') }}
                </span>
                {{ $t('mayday-web.search-empty-filters') }}</strong
              >
            </h2>
          </span>
        </div>
      </div>
    </div>

    <!-- NAVIGATION DISPLAY -->
    <div class="col-12 navigation-main-card" v-else v-loading="loading">
      <div v-if="isCase">
        <div v-if="displayCaseChildrenAndContents">
          <div
            class="px-2 mw-node-card-main-item justify-content-between align-items-start m-2"
            :class="{ hover: hovered === index }"
            v-for="(item, index) in children"
            :key="item.id"
            @mouseenter="onHover(index)"
            @mouseleave="hovered = -1"
            @click.prevent="
              goToWebTranslatedEntity({
                entityId: item.id,
                entityType: item.type || 'Case',
                query: { source: 'navigation' },
                open: $event.metaKey || $event.ctrlKey,
              })
            "
          >
            <div class="w-90 d-flex align-items-start">
              <div class="icon-wrapper">
                <font-awesome-icon
                  v-if="!item.type"
                  class="large-icon grey"
                  :icon="['fad', 'folder']"
                />
                <font-awesome-icon
                  v-else-if="item.type === 'Step'"
                  class="large-icon blue fa-rotate-90"
                  :icon="['fas', 'level-up']"
                />
                <font-awesome-icon
                  v-else-if="item.type === 'keyStep'"
                  class="large-icon blue"
                  :icon="['fad', 'key']"
                />
                <img v-else :src="getIcon(item.type)" alt class="large-icon" />
              </div>

              <div class="w-100">
                <el-tooltip
                  :disabled="isTooltipDisabled(item.id)"
                  :content="item.label"
                  placement="top"
                  effect="light"
                >
                  <h3 class="mb-0 text-overflow" :ref="item.id">
                    {{ item.label }}
                  </h3>
                </el-tooltip>
                <div
                  class="d-flex flex-wrap align-items-center"
                  v-if="item.labels && item.labels.length"
                >
                  <ContentListItemFullLabelWrapper :labels="item.labels" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center">
          <img id="gif" src="@/assets/empty-folder.svg" />
          <h2 class="search-empty-state mt-2 mb-3">
            {{ $t('mayday-web.case-empty') }}
          </h2>
        </div>
      </div>

      <!-- EDITORS -->
      <div v-else class="my-2">
        <AiAgentCollapse
          v-if="displaySummary() || displayActions"
          :summary="getSummary()"
          :action-items="actionItems"
        />
        <DiagnosticView v-if="isDiag" />
        <ArticleView v-else-if="isArticle" />
      </div>
      <!-- SATISFACTION -->
      <div>
        <!-- v-if="!editable && !isArchive" -->
        <SatisfactionActions
          v-if="isLastContent"
          :content-id="focusNavItem.id"
          :content="focusNavItem"
          @open-feedback-modal="openFeedbackModal(focusItem)"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';

import DiagnosticView from './DiagnosticView';
import DiagnosticProgressBar from './DiagnosticProgressBar';
import ArticleView from './ArticleView';
import ContentListItemFullLabelWrapper from '@/components/ListItems/ContentListItemFullLabelWrapper';
import ExplainPromptPopover from '@/components/SuperSearch/ExplainPromptPopover';
import SatisfactionActions from '../../../../KnowledgeEditor/ParametricContentBuilder/SatisfactionActions';
import Breadcrumb from '@/components/SuperSearch/Breadcrumb';
import ProductParameters from '@/components/SuperSearch/ProductParameters';
import AiAgentCollapse from 'components/AI/AiAgentCollapse.vue';
import emojiStrip from '@/utils/emojiStrip';

export default {
  name: 'mayday-node-main-body',
  components: {
    AiAgentCollapse,
    DiagnosticView,
    ArticleView,
    ExplainPromptPopover,
    DiagnosticProgressBar,
    SatisfactionActions,
    ContentListItemFullLabelWrapper,
    Breadcrumb,
    ProductParameters,
  },
  data() {
    return {
      hovered: -1,
      openedSummaries: [],
    };
  },
  computed: {
    ...mapGetters(['isSplitKnowledgeViewer']),
    ...mapGetters('webKnowledgeModule', [
      'focusNavItem',
      'focusDiagItem',
      'searchFilters',
      'searchResults',
      'searchSize',
      'searchTotalCount',
      'showSearch',
      'searchQuery',
      'loading',
    ]),
    labelSearchedIds() {
      return this.searchFilters.reduce((acc, tag) => {
        if (tag.key === 'categories') acc.push(tag.id);
        return acc;
      }, []);
    },
    focusItem() {
      return this.isDiag ? this.focusDiagItem : this.focusNavItem;
    },
    isCase() {
      return this.focusNavItem && !this.focusNavItem.type;
    },
    isDiag() {
      return (
        this.focusNavItem &&
        (this.focusNavItem.type === 'Diagnostic' ||
          this.focusNavItem.type === 'Step')
      );
    },
    isLastContent() {
      return !this.isCase && this.focusItem && !this.focusItem.children.length;
    },
    isArticle() {
      return this.focusNavItem && this.focusNavItem.type === 'Article';
    },
    children() {
      if (!this.focusNavItem) return [];
      const children = this.focusNavItem.orderedChildrenAndContents || [];
      if (!this.isSplitKnowledgeViewer) return children;
      return children.sort((a, b) =>
        emojiStrip(a.label).localeCompare(emojiStrip(b.label)),
      );
    },
    displaySearchResults() {
      return this.showSearch && this.searchResults.length > 0;
    },
    hasMore() {
      return this.searchSize < this.searchTotalCount;
    },
    displayCaseChildrenAndContents() {
      return (
        this.isCase && this.focusNavItem.orderedChildrenAndContents.length > 0
      );
    },
    displayActions() {
      if (!this.focusItem) return false;
      return (
        !!this.focusItem.actionItems &&
        !!this.focusItem.actionItems.isVisible &&
        !!this.focusItem.actionItems.body &&
        !!this.focusItem.actionItems.body.length
      );
    },
    actionItems() {
      if (!this.focusItem) return null;
      return this.displayActions ? this.focusItem.actionItems : null;
    },
  },
  watch: {
    displaySearchResults() {
      this.hovered = -1;
    },
  },
  methods: {
    openFeedbackModal(payload) {
      this.$emit('open-feedback-modal', payload);
    },
    onHover(index) {
      this.hovered = index;
    },
    getIcon(type) {
      switch (type) {
        case 'Article':
          return require('@/assets/article-icon-v2.svg');
        case 'Diagnostic':
          return require('@/assets/diag-icon-v2.svg');
        default:
          return require('@/assets/article-icon-v2.svg');
      }
    },
    getBoostedLevel(userBoost) {
      switch (userBoost) {
        case '90':
          return {
            customStyle: { 'background-color': '#8CB369' },
            tooltip: 'knowledge.search-admin.user-boost.high',
          };
        case '50':
          return {
            customStyle: { 'background-color': '#fb7901' },
            tooltip: 'knowledge.search-admin.user-boost.medium',
          };
        case '1':
          return {
            customStyle: { 'background-color': 'grey' },
            tooltip: 'knowledge.search-admin.user-boost.low',
          };
        default:
          return {};
      }
    },
    clickItem(item, itemIndex, e) {
      this.storeSearchSuccessEvent({ item, itemIndex });
      const open = e.metaKey || e.ctrlKey;
      if (!open && this.focusItem && item.id === this.focusItem.id) {
        this.initSearch();
        return;
      }
      this.goToWebTranslatedEntity({
        entityId: item.id,
        entityType: item.type || 'Case',
        query: { source: 'search' },
        open,
      });
    },
    toggleSummary(id) {
      if (this.openedSummaries.includes(id)) {
        this.openedSummaries = this.openedSummaries.filter((i) => i !== id);
      } else {
        this.openedSummaries.push(id);
      }
    },
    handleKeyPress(e) {
      const listSize = this.displaySearchResults
        ? this.searchResults.length - 1
        : this.displayCaseChildrenAndContents
        ? this.children.length - 1
        : 0;
      if (e.key === 'Enter' && this.hovered >= 0 && this.hovered <= listSize) {
        e.preventDefault();
        if (this.displaySearchResults || this.displayCaseChildrenAndContents) {
          const item = this.displaySearchResults
            ? this.searchResults[this.hovered]
            : this.children[this.hovered];

          if (this.displaySearchResults) {
            this.storeSearchSuccessEvent({ item, itemIndex: this.hovered });
          }

          this.goToWebTranslatedEntity({
            item,
            entityId: item.id,
            entityType: item.type || 'Case',
            open: e.metaKey || e.ctrlKey,
            query: {
              source: this.displaySearchResults ? 'search' : 'navigation',
            },
          });
        }
      } else if (
        e.key === 'ArrowUp' &&
        (this.displaySearchResults || this.displayCaseChildrenAndContents)
      ) {
        this.hovered = this.hovered > 0 ? this.hovered - 1 : listSize;
      } else if (
        e.key === 'ArrowDown' &&
        (this.displaySearchResults || this.displayCaseChildrenAndContents)
      ) {
        this.hovered = this.hovered < listSize ? this.hovered + 1 : 0;
      }
    },
    isTooltipDisabled(id) {
      const textEl = this.$refs[`${id}`];
      if (!textEl || !textEl[0]) return true;
      return textEl[0].scrollWidth <= textEl[0].clientWidth;
    },
    displaySummary(item = this.focusItem) {
      if (!item) return false;
      return (
        (!!item.summary &&
          !!item.summary.isVisible &&
          !!item.summary.body &&
          !!item.summary.body.length) ||
        (!!item.shortSummary &&
          !!item.shortSummary.isVisible &&
          !!item.shortSummary.body &&
          !!item.shortSummary.body.length)
      );
    },
    getSummary(item = this.focusItem) {
      if (item.summary.isVisible) {
        return item.summary.body;
      } else if (item.shortSummary.isVisible) {
        return item.shortSummary.body;
      }
      return null;
    },
    ...mapActions('webKnowledgeModule', [
      'goToWebTranslatedEntity',
      'storeSearchSuccessEvent',
      'searchDisplayMore',
      'initSearch',
    ]),
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyPress);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyPress);
  },
};
</script>
<style lang="scss" scoped>
.mw-node-main-card {
  background-color: white;
  border: 2px solid #f2f6fd;
  border-radius: 4px;

  .mw-node-card-header {
    height: 60px;
    border-bottom: 2px solid #f2f6fd;

    h2 {
      font-size: 22px;
      letter-spacing: 1px;
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .icon-wrapper {
    height: 27px;
    display: flex;
    align-items: center;
  }

  .large-icon {
    margin-right: 0.5rem;
    width: 16px;
    height: 16px;
  }

  .blue {
    color: $blue-mayday;
  }

  .grey {
    color: $grey-4-mayday;
  }

  .w-90 {
    max-width: 90%;
  }

  .mw-node-card-main {
    overflow: auto;
    overflow-x: hidden;
    margin-top: -2px;
    .mw-node-card-main-item {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding-top: 8px;
      padding-bottom: 8px;
      cursor: pointer;

      .mw-node-card-main-item-icon {
        width: 30px;
        height: 30px;
      }
      h3 {
        font-size: 18px;
      }
    }
  }
}
.hover {
  border-radius: 2px;
  background: #f2f6fd;
  .grey {
    color: $grey-5-mayday;
  }
}
.navigation-main-card {
  min-height: calc(100vh - 280px);
}
.star-content {
  visibility: hidden;
}
.diagnostic-progress-bar {
  width: 100%;
}

.search-empty-state {
  color: $grey-5-mayday;
}

.search-icon {
  color: black;
  background-color: $grey-4-mayday;
  border-radius: 4px;
  font-size: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 56px;
  height: 56px;
}

.summary-button {
  font-size: 12px;
  flex: 0;
  white-space: nowrap;
  color: $grey-8-mayday;
  margin-right: 4px;
  &.opened,
  &:hover {
    color: $blue-mayday;
  }
}

.summary-icon {
  width: 14px;
  height: 14px;
}

.search-summary {
  background-color: $grey-1-mayday;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  color: $grey-8-mayday;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.2s ease-in-out, margin 0.2s ease-in-out;
  margin: 0;

  div {
    padding: 6px 12px;
  }

  &.hovered {
    background-color: white;
  }

  &.opened {
    max-height: 120px;
    overflow: auto;
    margin: 4px 0;
  }
}

#gif {
  max-width: 50vh;
  border-radius: 5px;
}

.label-row {
  display: flex;
  align-items: left;
  width: 90%;
}

.text-overflow {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.full-labels {
  margin-top: 4px;
}
.explain-prompt {
  display: inline-flex;
  line-height: 1px;
}
.user-boosted-result {
  border-radius: 50px;
  height: 12px;
  width: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 25px;
  top: 0px;
  cursor: pointer;
}
.user-boosted-result-icon {
  width: 8px;
  height: 8px;
  color: white;
}
</style>
